import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  BackButton,
  ThinTextField,
  ThinDropdown,
  AddButton,
} from "../../common/GeneralComponent";
import { fetchData } from "../../../utils/functions";
import { useFormik } from "formik";
import * as Yup from "yup";
import Loader from "../../../components/partials/Loader";
import { toast } from "react-toastify";

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB

const EditFeature = () => {
  const { id } = useParams();
  const [loader, setLoader] = useState(true);
  const [imagePreview, setImagePreview] = useState(null);
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: {
      feature_id: "",
      feature_name: "",
      image: null,
      status: 1,
    },
validationSchema: Yup.object({
  feature_name: Yup.string()
    .required("Required")
    .min(3, "Minimum 3 characters")
    .max(100, "Feature name must be 100 characters or less"), 
  status: Yup.number()
    .oneOf([0, 1], "Invalid status")
    .required("Status is required"),
}),
    onSubmit: async (values) => {
      try {
        setLoader(true);
        const formData = new FormData();
        formData.append("feature_id", values.feature_id);
        formData.append("feature_name", values.feature_name);
        formData.append("status", values.status);
        formData.append("updated_by", 1); // hardcoded, adjust if needed

        if (values.image instanceof File) {
          formData.append("image", values.image);
        }

        await fetchData("features", "PUT", formData, {
          "Content-Type": "multipart/form-data",
        });

        toast.success("Updated Successfully");
        navigate("/features");
      } catch (error) {
        console.error("Feature update failed:", error);
        toast.error("Update failed");
      } finally {
        setLoader(false);
      }
    },
  });

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!["image/png", "application/pdf"].includes(file.type)) {
      toast.error("Only PNG or PDF files allowed");
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      toast.error("File must be less than 2MB");
      return;
    }

    formik.setFieldValue("image", file);

    if (file.type === "image/png") {
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setImagePreview(null);
    }
  };

  useEffect(() => {
    const fetchFeature = async () => {
      try {
        const res = await fetchData(`features?id=${id}`, "GET");
        const feature = res.data || res;

        formik.setValues({
          feature_id: feature.feature_id,
          feature_name: feature.feature_name || "",
          image: feature.image || "",
          status: feature.status ?? 1,
        });

        if (feature.image) {
          const fullUrl = feature.image.startsWith("http")
            ? feature.image
            : `${process.env.REACT_APP_BASE_URL || "http://localhost:8000"}/${feature.image}`;

          if (feature.image.toLowerCase().endsWith(".png")) {
            setImagePreview(fullUrl);
          } else if (feature.image.toLowerCase().endsWith(".pdf")) {
            setImagePreview(null); // no thumbnail, show filename instead
          }
        }

        setLoader(false);
      } catch (error) {
        console.error("Error fetching feature:", error);
        setLoader(false);
      }
    };

    fetchFeature();
  }, [id]);

  return (
    <div className="w-full my-4 mx-auto">
      {/* <BackButton to="/features" text="Back" />
      <h1 className="text-black text-[1.475rem] text-Black font-[Bold] ">
        Edit Feature
      </h1> */}

      <div className="flex  lg:flex-row flex-col lg:justify-between justify-start lg:items-center items-start gap-3 w-full">
        <h1 className="lg:text-[1.475rem] text-[1rem] text-[#212529] font-[Bold]">
          Edit Feature
        </h1>
        <BackButton text="Back" to="/features" />
      </div>
      {/* {loader && <Loader />} */}

      <div className="bg-white rounded-lg shadow w-full lg:mb-[30px] mt-4  mb-[7rem]">
        <div className="p-4 flex md:flex-row rounded-t-lg gradiant2 flex-col gap-3 justify-between md:items-center border-lightGray border-b ">
          <h1 className="lg:text-[0.875rem] text-[13px]  tracking-[0.0625rem] text-[#856800] font-[bold] uppercase">
            Edit Feature
          </h1>
        </div>
        <form onSubmit={formik.handleSubmit} className="p-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 p-4 my-4">
            <ThinTextField
              label="Feature Name"
              name="feature_name"
              value={formik.values.feature_name}
              onChange={formik.handleChange}
              error={formik.touched.feature_name && formik.errors.feature_name}
              helperText={formik.touched.feature_name && formik.errors.feature_name}
            />
            <div className="flex flex-col w-full">
              <label className="text-sm font-medium text-gray mb-1">
                Upload Image (PNG or PDF)
              </label>
              <input
                type="file"
                accept=".png,.pdf"
                onChange={handleFileChange}
                className="`text-[15px] font-[InterLight] text-[#565656] bg-[#ebeef2] focus:bg-[#dde2e9] py-2 px-4 outline-none border-[#dde2e9] border rounded-[0.35rem]"
              />
              {formik.errors.image && (
                <p className="text-red-500 text-sm">{formik.errors.image}</p>
              )}
              {formik.values.image && (
                <div className="mt-2">
                  {imagePreview ? (
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="h-32 border rounded object-contain"
                    />
                  ) : (
                    <p className="text-gray-600 text-sm">
                      📄{" "}
                      {typeof formik.values.image === "string"
                        ? formik.values.image.split("/").pop()
                        : formik.values.image.name}
                    </p>
                  )}
                </div>
              )}
            </div>

            <ThinDropdown
              label="Status"
              options={[
                { value: 1, label: "Active" },
                { value: 0, label: "Inactive" },
              ]}
              value={formik.values.status}
              onChange={(e) =>
                formik.setFieldValue("status", parseInt(e.target.value))
              }
            />
          </div>
          <div className="p-4">
            <AddButton text="Submit" type="Submit" />
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditFeature;
